import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import AdminShell from './AdminShell'
import VolleyPaymentIssues from './VolleyPaymentIssues'
import HistoricalCompletionPanel from './HistoricalCompletionPanel'
import { orderDeliveryLabel as deliveryLabel, ordersScopeDescription } from './ordersScope'
import { operationalWeekendLabels } from '../../shared/operationalWeek.ts'
import { NEXT_STOP_SMS_TEXT, shouldOfferNextStopSms, type DeliverySmsView } from '../../shared/deliverySms.ts'
import {
  AdminButton,
  AdminCard,
  AdminConfirmDialog,
  AdminDataTable,
  AdminField,
  AdminFilterBar,
  AdminInput,
  AdminModal,
  AdminPagination,
  AdminSelect,
  AdminSkeleton,
  AdminStatePanel,
  AdminStatusPill,
  AdminToastRegion,
  type AdminTableColumn,
  type AdminToast,
} from './AdminUi'
import '../styles/admin-orders.css'

type SessionPayload = {
  authenticated: true
  user: { email: string; role: string }
  csrfToken: string
}

type OrderView = 'active' | 'awaiting' | 'history'
type PaymentMethod = 'card' | 'bank-transfer'
type PaymentStatus = 'paid' | 'pending'
type FulfilmentStatus = 'received' | 'preparing' | 'packed' | 'out_for_delivery' | 'delivered'
type DeliveryFilter = 'all' | 'saturday' | 'sunday' | 'unspecified'
type FulfilmentFilter = 'all' | FulfilmentStatus | 'legacy'

const FULFILMENT_STATUSES: FulfilmentStatus[] = ['received', 'preparing', 'packed', 'out_for_delivery', 'delivered']

type OrderSummary = {
  orderId: string
  viewable: boolean
  customerName: string
  paymentMethod: PaymentMethod
  paymentProvider?: 'legacy' | 'volley'
  paymentStatus: PaymentStatus
  bankTransferDueAt: string | null
  bankTransferOverdue: boolean
  cancelledAt: string | null
  totalCents: number
  createdAt: string
  deliveryDay: 'saturday' | 'sunday' | null
  suburb: string
  boxCount: number
  fulfilmentStatus: FulfilmentStatus | null
}

type OrdersPayload = {
  view: OrderView
  drop: { startsAt: string; endsAt: string; timeZone: 'Pacific/Auckland' }
  page: number
  total: number
  totalPages: number
  counts: { active: number; awaiting: number; history: number }
  filters: { delivery: DeliveryFilter; fulfilment: FulfilmentFilter }
  orders: OrderSummary[]
}

type LiveProductTotalsPayload = {
  generatedAt: string
  drop: { startsAt: string; endsAt: string; timeZone: 'Pacific/Auckland' }
  liveOrderCount: number
  totalCookies: number
  groups: Array<{
    category: string
    totalCookies: number
    items: Array<{ productName: string; quantity: number }>
  }>
  addOns: {
    totalPacks: number
    items: Array<{
      sourceId: string
      productName: string
      packLabel: string
      quantity: number
    }>
  }
}

type ComplimentaryCatalogueItem = {
  sourceKind: 'product' | 'addon'
  sourceId: string
  name: string
  packLabel: string
  group: 'Products' | 'Add-ons'
}

type ComplimentaryItem = {
  id: string
  sourceKind: 'product' | 'addon'
  sourceId: string
  name: string
  packLabel: string
  quantity: number
  reason: string
  chargeCents: 0
  createdAt: string
  createdBy: string
  removedAt: string | null
  removedBy: string | null
}

type OrderDetail = {
  paymentProvider?: 'legacy' | 'volley'
  orderId: string
  deliverySms?: DeliverySmsView
  paymentMethod: PaymentMethod
  paymentStatus: PaymentStatus
  paymentReference: string | null
  subtotalCents: number
  discountCents: number
  discountCode: string | null
  deliveryFeeCents: number
  totalCents: number
  currency: 'NZD'
  createdAt: string
  measurement: null | {
    eligible: boolean | null
    consentBasis: 'explicit-grant' | 'nz-default' | 'explicit-deny' | 'not-permitted' | null
    browserPurchase: {
      state: 'queued' | 'not-permitted' | 'not-configured' | 'suppressed' | 'not-attempted'
      attemptedAt: string | null
    }
    serverPurchase: {
      state: 'pending' | 'sending' | 'retry' | 'sent' | 'failed' | 'suppressed' | 'not-applicable' | 'not-recorded'
      eventId: string | null
      attempts: number
      lastAttemptAt: string | null
      sentAt: string | null
      providerEventsReceived: number | null
      providerTraceId: string | null
      providerHttpStatus: number | null
      providerErrorCode: string | null
      providerErrorMessage: string | null
    }
    attribution: {
      utmSource: string | null
      utmMedium: string | null
      utmCampaign: string | null
      utmContent: string | null
      utmTerm: string | null
      landingPath: string | null
      capturedAt: string | null
      metaClickIdentified: boolean
      browserIdentifierPresent: boolean
    }
  }
  bankTransferDueAt: string | null
  bankTransferOverdue: boolean
  cancelledAt: string | null
  cancelledBy: string | null
  cancellationReason: string | null
  customer: { fullName: string; email: string; phone: string }
  delivery: {
    address: string
    suburb: string
    postcode: string
    day: 'saturday' | 'sunday' | null
    notes: string
  }
  boxes: Array<{
    quantity: number
    priceCents: number
    selections: Array<{
      productName: string
      category: string
      quantity: number
      pricingLabel: 'Included in box'
    }>
  }>
  addons: Array<{
    id: string
    name: string
    packLabel: string
    unitPriceCents: number
    quantity: number
  }>
  internalNote: {
    text: string
    updatedAt: string
    updatedBy: string
  } | null
  complimentaryItems: ComplimentaryItem[]
  complimentaryHistory: ComplimentaryItem[]
  canMarkPaid: boolean
  canCancel: boolean
  fulfilment: {
    tracked: boolean
    status: FulfilmentStatus | null
    updatedAt: string | null
    canStart: boolean
    startStatuses: FulfilmentStatus[]
    nextStatus: FulfilmentStatus | null
    canAdvance: boolean
    correctionTargets: FulfilmentStatus[]
    message: string
    history: Array<{
      previousStatus: FulfilmentStatus | null
      newStatus: FulfilmentStatus
      actor: string
      eventType: 'initial-receipt' | 'legacy-start' | 'progress' | 'correction'
      reason: string | null
      createdAt: string
    }>
  }
}

const NZD = new Intl.NumberFormat('en-NZ', {
  style: 'currency',
  currency: 'NZD',
  minimumFractionDigits: 2,
})
const DATE_TIME = new Intl.DateTimeFormat('en-NZ', {
  timeZone: 'Pacific/Auckland',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
})

function formatMoney(cents: number) { return NZD.format(cents / 100) }
function formatDateTime(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : DATE_TIME.format(date)
}
function paymentMethodLabel(method: PaymentMethod) { return method === 'bank-transfer' ? 'Bank transfer' : 'Card' }
function measurementEligibilityLabel(value: boolean | null) {
  if (value === true) return 'Eligible'
  if (value === false) return 'Suppressed'
  return 'Not recorded'
}
function measurementStateLabel(value: string) {
  return value.split('-').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
}
function liveCategoryLabel(category: string) {
  if (category === 'Brownie Cookie Sandwich') return 'Brownie Cookie Sandwiches'
  if (category === 'Stuffed Cookie') return 'Massive Stuffed Cookies'
  if (category === 'Brownie') return 'Brownies'
  return category || 'Other'
}
function fulfilmentLabel(status: FulfilmentStatus | null) {
  if (status === 'received') return 'Received'
  if (status === 'preparing') return 'Preparing'
  if (status === 'packed') return 'Packed / Ready'
  if (status === 'out_for_delivery') return 'Out for delivery'
  if (status === 'delivered') return 'Delivered'
  return 'Legacy'
}
function fulfilmentTone(status: FulfilmentStatus | null): 'neutral' | 'success' | 'warning' | 'info' | 'pink' {
  if (status === 'delivered') return 'success'
  if (status === 'out_for_delivery') return 'info'
  if (status === 'packed') return 'pink'
  if (status === 'preparing') return 'warning'
  return 'neutral'
}
function nextActionLabel(status: FulfilmentStatus | null) {
  if (status === 'received') return 'Start preparing'
  if (status === 'preparing') return 'Mark packed / ready'
  if (status === 'packed') return 'Mark out for delivery'
  if (status === 'out_for_delivery') return 'Mark delivered'
  return 'Update fulfilment'
}
function deliveredEmailOutcome(status: string | undefined) {
  if (status === 'suppressed-historical') return 'This historical completion permanently suppresses the customer Delivered email.'
  if (status === 'intervention') return 'The order is Delivered. The customer email needs owner review in Email & Notifications.'
  if (status === 'queued-retry' || status === 'pending') {
    return 'The order is Delivered. The customer email is queued for safe retry.'
  }
  if (status === 'in-progress') {
    return 'The order is Delivered. The customer email is already being processed.'
  }
  return 'The customer Delivered email has been triggered.'
}
function progressClass(current: FulfilmentStatus | null, step: FulfilmentStatus) {
  if (!current) return ''
  const currentIndex = FULFILMENT_STATUSES.indexOf(current)
  const stepIndex = FULFILMENT_STATUSES.indexOf(step)
  if (step === 'received') return 'is-complete'
  if (currentIndex > stepIndex) return 'is-complete'
  if (currentIndex === stepIndex) return 'is-current'
  return ''
}
function fulfilmentEventLabel(event: OrderDetail['fulfilment']['history'][number]) {
  if (event.eventType === 'initial-receipt') return 'Order received'
  if (event.eventType === 'legacy-start') return `Legacy tracking started at ${fulfilmentLabel(event.newStatus)}`
  if (event.eventType === 'correction') return `Corrected to ${fulfilmentLabel(event.newStatus)}`
  return `Moved to ${fulfilmentLabel(event.newStatus)}`
}
function complimentaryKey(item: Pick<ComplimentaryCatalogueItem, 'sourceKind' | 'sourceId'>) {
  return `${item.sourceKind}:${item.sourceId}`
}

function environmentFromHostname(hostname: string) {
  const host = hostname.toLowerCase()
  if (host.includes('staging')) return 'Staging'
  if (host === 'localhost' || host === '127.0.0.1') return 'Local'
  return undefined
}
function PaymentPill({ status, cancelledAt, overdue = false }: { status: PaymentStatus; cancelledAt?: string | null; overdue?: boolean }) {
  if (cancelledAt) return <AdminStatusPill tone="danger">Cancelled</AdminStatusPill>
  if (status === 'paid') return <AdminStatusPill tone="success">Paid</AdminStatusPill>
  return <AdminStatusPill tone={overdue ? 'danger' : 'warning'}>{overdue ? 'Payment window expired' : 'Awaiting payment'}</AdminStatusPill>
}

function initialOrdersSearch() {
  return (new URLSearchParams(window.location.search).get('search') ?? '').trim().slice(0, 80)
}

function initialOrdersView(): OrderView {
  const value = new URLSearchParams(window.location.search).get('view')
  return value === 'history' || value === 'awaiting' ? value : 'active'
}

export default function AdminOrdersPage() {
  const [historicalCompletionOpen, setHistoricalCompletionOpen] = useState(false)
  const [session, setSession] = useState<SessionPayload | null>(null)
  const [data, setData] = useState<OrdersPayload | null>(null)
  const [view, setView] = useState<OrderView>(() => initialOrdersView())
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState(() => initialOrdersSearch())
  const [search, setSearch] = useState(() => initialOrdersSearch())
  const [deliveryFilter, setDeliveryFilter] = useState<DeliveryFilter>('all')
  const [fulfilmentFilter, setFulfilmentFilter] = useState<FulfilmentFilter>('all')
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null)
  const [smsPromptOrder, setSmsPromptOrder] = useState<string | null>(null)
  const smsActionRef = useRef(false)
  const orderTriggerRef = useRef<HTMLButtonElement | null>(null)
  const [liveTotals, setLiveTotals] = useState<LiveProductTotalsPayload | null>(null)
  const [liveTotalsError, setLiveTotalsError] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [logoutBusy, setLogoutBusy] = useState(false)
  const [markPaidConfirm, setMarkPaidConfirm] = useState(false)
  const [cancelConfirm, setCancelConfirm] = useState(false)
  const [fulfilmentAdvanceConfirm, setFulfilmentAdvanceConfirm] = useState(false)
  const [fulfilmentStartConfirm, setFulfilmentStartConfirm] = useState(false)
  const [fulfilmentCorrectionConfirm, setFulfilmentCorrectionConfirm] = useState(false)
  const [fulfilmentEditor, setFulfilmentEditor] = useState<'start' | 'correct' | null>(null)
  const [fulfilmentTarget, setFulfilmentTarget] = useState<FulfilmentStatus>('received')
  const [fulfilmentReason, setFulfilmentReason] = useState('')
  const [internalNoteDraft, setInternalNoteDraft] = useState('')
  const [complimentaryCatalogue, setComplimentaryCatalogue] = useState<ComplimentaryCatalogueItem[]>([])
  const [complimentaryEditorOpen, setComplimentaryEditorOpen] = useState(false)
  const [complimentarySelection, setComplimentarySelection] = useState('')
  const [complimentaryQuantity, setComplimentaryQuantity] = useState(1)
  const [complimentaryReason, setComplimentaryReason] = useState('')
  const [complimentaryRemoveTarget, setComplimentaryRemoveTarget] = useState<ComplimentaryItem | null>(null)
  const [toasts, setToasts] = useState<AdminToast[]>([])
  const environmentLabel = useMemo(() => environmentFromHostname(window.location.hostname), [])

  async function loadOrders(targetView = view, targetPage = page, targetSearch = search, targetDelivery = deliveryFilter, targetFulfilment = fulfilmentFilter) {
    setBusy(true)
    setError('')
    try {
      const params = new URLSearchParams({ view: targetView, page: String(targetPage) })
      if (targetSearch) params.set('search', targetSearch)
      if (targetDelivery !== 'all') params.set('delivery', targetDelivery)
      if (targetFulfilment !== 'all') params.set('fulfilment', targetFulfilment)
      const [sessionResponse, ordersResponse] = await Promise.all([
        fetch('/api/admin/session', { credentials: 'same-origin' }),
        fetch(`/api/admin/orders?${params.toString()}`, { credentials: 'same-origin', cache: 'no-store' }),
      ])
      if (sessionResponse.status === 401 || ordersResponse.status === 401) {
        window.location.replace('/admin/login?next=/admin/orders')
        return
      }
      if (!sessionResponse.ok || !ordersResponse.ok) throw new Error('orders unavailable')
      const nextSession = await sessionResponse.json() as SessionPayload
      const nextData = await ordersResponse.json() as OrdersPayload
      setSession(nextSession)
      setData(nextData)
      if (nextData.page !== targetPage) setPage(nextData.page)
    } catch {
      setError('Orders could not be loaded. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  async function loadLiveProductTotals() {
    try {
      const response = await fetch('/api/admin/orders/live-product-totals', {
        credentials: 'same-origin',
        cache: 'no-store',
      })
      if (response.status === 401) {
        window.location.replace('/admin/login?next=/admin/orders')
        return
      }
      if (!response.ok) throw new Error('live totals unavailable')
      setLiveTotals(await response.json() as LiveProductTotalsPayload)
      setLiveTotalsError('')
    } catch {
      setLiveTotalsError('Live totals could not refresh. The last successful totals remain on screen.')
    }
  }

  async function loadOrderDetail(orderId: string) {
    setError('')
    try {
      const [response, catalogueResponse] = await Promise.all([
        fetch(`/api/admin/orders/${encodeURIComponent(orderId)}`, {
          credentials: 'same-origin',
          cache: 'no-store',
        }),
        fetch('/api/admin/orders/complimentary-catalogue', {
          credentials: 'same-origin',
          cache: 'no-store',
        }),
      ])
      if (response.status === 401 || catalogueResponse.status === 401) {
        window.location.replace('/admin/login?next=/admin/orders')
        return
      }
      if (!response.ok) throw new Error('order unavailable')
      const order = (await response.json() as { order: OrderDetail }).order
      setSelectedOrder(order)
      setInternalNoteDraft(order.internalNote?.text ?? '')
      if (catalogueResponse.ok) {
        const catalogue = (await catalogueResponse.json() as { items: ComplimentaryCatalogueItem[] }).items
        setComplimentaryCatalogue(catalogue)
        setComplimentarySelection((current) => current || (catalogue[0] ? complimentaryKey(catalogue[0]) : ''))
      }
      return order
    } catch {
      setError('That order could not be loaded. Please try again.')
    }
  }

  useEffect(() => { void loadOrders(view, page, search, deliveryFilter, fulfilmentFilter) }, [view, page, search, deliveryFilter, fulfilmentFilter])

  useEffect(() => {
    if (!session) return
    void loadLiveProductTotals()
    const timer = window.setInterval(() => { void loadLiveProductTotals() }, 30_000)
    return () => window.clearInterval(timer)
  }, [session?.user.email])

  async function logout() {
    if (!session || logoutBusy) return
    setLogoutBusy(true)
    setError('')
    try {
      const response = await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': session.csrfToken },
      })
      if (!response.ok) throw new Error('logout failed')
      window.location.replace('/admin/login')
    } catch {
      setError('Logout could not be completed. Please try again.')
      setLogoutBusy(false)
    }
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault()
    setPage(1)
    setSearch(searchInput.trim())
  }

  function selectView(nextView: OrderView) {
    if (nextView === view) return
    setPage(1)
    setView(nextView)
  }

  function pushToast(title: string, message: string) {
    const id = crypto.randomUUID()
    setToasts([{ id, title, message, tone: 'success' }])
    window.setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 4_000)
  }

  async function saveInternalNote() {
    if (!session || !selectedOrder || busy) return
    setBusy(true)
    setError('')
    const orderId = selectedOrder.orderId
    try {
      const response = await fetch(`/api/admin/orders/${encodeURIComponent(orderId)}/internal-note`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': session.csrfToken },
        body: JSON.stringify({ note: internalNoteDraft }),
      })
      const payload = await response.json().catch(() => null) as { message?: string } | null
      if (response.status === 401) {
        window.location.replace('/admin/login?next=/admin/orders')
        return
      }
      if (!response.ok) throw new Error(payload?.message || 'Internal note could not be saved.')
      await loadOrderDetail(orderId)
      pushToast('Internal note saved', 'This note is visible only inside Boss Zone Orders.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Internal note could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  async function addComplimentaryItem() {
    if (!session || !selectedOrder || busy) return
    const selected = complimentaryCatalogue.find((item) => complimentaryKey(item) === complimentarySelection)
    if (!selected) {
      setError('Choose a product or add-on.')
      return
    }
    setBusy(true)
    setError('')
    const orderId = selectedOrder.orderId
    try {
      const response = await fetch(`/api/admin/orders/${encodeURIComponent(orderId)}/complimentary-items`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': session.csrfToken },
        body: JSON.stringify({
          sourceKind: selected.sourceKind,
          sourceId: selected.sourceId,
          quantity: complimentaryQuantity,
          reason: complimentaryReason,
        }),
      })
      const payload = await response.json().catch(() => null) as { message?: string } | null
      if (response.status === 401) {
        window.location.replace('/admin/login?next=/admin/orders')
        return
      }
      if (!response.ok) throw new Error(payload?.message || 'Complimentary item could not be added.')
      await loadOrderDetail(orderId)
      setComplimentaryEditorOpen(false)
      setComplimentaryQuantity(1)
      setComplimentaryReason('')
      pushToast('Complimentary item added', 'It is marked FREE and does not change the customer total.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Complimentary item could not be added.')
    } finally {
      setBusy(false)
    }
  }

  async function removeComplimentaryItem() {
    if (!session || !selectedOrder || !complimentaryRemoveTarget || busy) return
    const target = complimentaryRemoveTarget
    setComplimentaryRemoveTarget(null)
    setBusy(true)
    setError('')
    const orderId = selectedOrder.orderId
    try {
      const response = await fetch(
        `/api/admin/orders/${encodeURIComponent(orderId)}/complimentary-items/${encodeURIComponent(target.id)}/remove`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'X-CSRF-Token': session.csrfToken },
        },
      )
      const payload = await response.json().catch(() => null) as { message?: string } | null
      if (response.status === 401) {
        window.location.replace('/admin/login?next=/admin/orders')
        return
      }
      if (!response.ok) throw new Error(payload?.message || 'Complimentary item could not be removed.')
      await loadOrderDetail(orderId)
      pushToast('Complimentary item removed', 'The removal remains recorded in order history.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Complimentary item could not be removed.')
    } finally {
      setBusy(false)
    }
  }

  async function markSelectedOrderPaid() {
    if (!session || !selectedOrder?.canMarkPaid || busy) return
    setMarkPaidConfirm(false)
    setBusy(true)
    setError('')
    const orderId = selectedOrder.orderId
    try {
      const response = await fetch(`/api/admin/orders/${encodeURIComponent(orderId)}/mark-paid`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': session.csrfToken },
      })
      const payload = await response.json().catch(() => null) as { message?: string; emailStatus?: string } | null
      if (response.status === 401) {
        window.location.replace('/admin/login?next=/admin/orders')
        return
      }
      if (!response.ok) throw new Error(payload?.message || 'Payment status could not be updated.')
      await Promise.all([loadOrders(view, page, search), loadOrderDetail(orderId)])
      pushToast(
        'Bank transfer marked paid',
        payload?.emailStatus === 'intervention'
          ? 'Payment is recorded as paid. The customer email needs owner review in Email & Notifications.'
          : payload?.emailStatus === 'queued-retry'
          ? 'Payment is recorded as paid. The Payment Received email is queued for safe retry.'
          : 'Payment is recorded as paid and the Payment Received email workflow has been triggered.',
      )
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Payment status could not be updated.')
    } finally {
      setBusy(false)
    }
  }

  async function cancelSelectedBankTransfer() {
    if (!session || !selectedOrder?.canCancel || busy) return
    setCancelConfirm(false)
    setBusy(true)
    setError('')
    const orderId = selectedOrder.orderId
    try {
      const response = await fetch(`/api/admin/orders/${encodeURIComponent(orderId)}/cancel-bank-transfer`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': session.csrfToken },
      })
      const payload = await response.json().catch(() => null) as { message?: string; emailStatus?: string } | null
      if (response.status === 401) {
        window.location.replace('/admin/login?next=/admin/orders')
        return
      }
      if (!response.ok) throw new Error(payload?.message || 'Order could not be cancelled.')
      await Promise.all([loadOrders(view, page, search), loadOrderDetail(orderId), loadLiveProductTotals()])
      pushToast(
        'Bank-transfer order cancelled',
        payload?.emailStatus === 'intervention'
          ? 'The order is cancelled. The customer email needs owner review in Email & Notifications.'
          : payload?.emailStatus === 'queued-retry'
          ? 'The order is cancelled. The customer cancellation email is queued for safe retry.'
          : 'The order is cancelled and the customer cancellation email has been triggered.',
      )
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Order could not be cancelled.')
    } finally {
      setBusy(false)
    }
  }

  async function refreshOrder(orderId: string) {
    const [, order] = await Promise.all([
      loadOrders(view, page, search, deliveryFilter, fulfilmentFilter),
      loadOrderDetail(orderId),
    ])
    return order
  }

  async function reviewNextStopSms() {
    if (!selectedOrder || busy || smsActionRef.current) return
    const orderId = selectedOrder.orderId
    setBusy(true)
    const order = await loadOrderDetail(orderId)
    if (order?.orderId === orderId && order.deliverySms?.canSend) setSmsPromptOrder(orderId)
    setBusy(false)
  }

  async function submitSmsAction(action: 'send' | 'check-status') {
    if (!selectedOrder || !session || busy || smsActionRef.current) return
    const orderId = selectedOrder.orderId
    if (action === 'send' && (smsPromptOrder !== orderId || !selectedOrder.deliverySms?.canSend || !selectedOrder.deliverySms.confirmation)) return
    smsActionRef.current = true
    setBusy(true)
    setError('')
    try {
      const response = await fetch(`/api/admin/orders/${encodeURIComponent(orderId)}/next-stop-sms`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': session.csrfToken },
        body: JSON.stringify(action === 'send'
          ? { action, confirmNextStop: true, confirmation: selectedOrder.deliverySms!.confirmation }
          : { action }),
      })
      if (response.status === 401) { window.location.replace('/admin/login?next=/admin/orders'); return }
      const payload = await response.json() as { deliverySms?: DeliverySmsView }
      if (!response.ok || !payload.deliverySms) throw new Error('sms-result-unconfirmed')
      setSelectedOrder(current => current?.orderId === orderId ? { ...current, deliverySms: payload.deliverySms } : current)
      setSmsPromptOrder(null)
    } catch {
      // A browser/network failure is NOT permission to retry a send.
      setSelectedOrder(current => current?.orderId === orderId ? { ...current, deliverySms: {
        state: 'unknown', message: 'The request outcome is unconfirmed. Check SMS status before taking any further action. Do not resend.',
        canSend: false, canCheckStatus: true, confirmation: null, recipientHint: current.deliverySms?.recipientHint ?? null,
      } } : current)
      setSmsPromptOrder(null)
    } finally {
      smsActionRef.current = false
      setBusy(false)
    }
  }

  async function startSelectedFulfilment() {
    if (!session || !selectedOrder || busy) return
    setFulfilmentStartConfirm(false)
    setBusy(true)
    setError('')
    const orderId = selectedOrder.orderId
    try {
      const response = await fetch(`/api/admin/orders/${encodeURIComponent(orderId)}/fulfilment/start`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': session.csrfToken },
        body: JSON.stringify({ expectedCurrentStatus: null, requestedStatus: fulfilmentTarget }),
      })
      const payload = await response.json().catch(() => null) as { message?: string; emailStatus?: string } | null
      if (response.status === 401) {
        window.location.replace('/admin/login?next=/admin/orders')
        return
      }
      if (!response.ok) {
        if (response.status === 409) await refreshOrder(orderId)
        throw new Error(payload?.message || 'Fulfilment tracking could not be started.')
      }
      await refreshOrder(orderId)
      setFulfilmentEditor(null)
      if (fulfilmentTarget === 'delivered') {
        pushToast('Order marked delivered', deliveredEmailOutcome(payload?.emailStatus))
      } else {
        pushToast('Fulfilment tracking started', `Order is now ${fulfilmentLabel(fulfilmentTarget)}.`)
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Fulfilment tracking could not be started.')
    } finally {
      setBusy(false)
    }
  }

  async function advanceSelectedFulfilment() {
    if (!session || !selectedOrder?.fulfilment.status || !selectedOrder.fulfilment.nextStatus || busy) return
    setFulfilmentAdvanceConfirm(false)
    setBusy(true)
    setError('')
    const orderId = selectedOrder.orderId
    const expectedCurrentStatus = selectedOrder.fulfilment.status
    const requestedStatus = selectedOrder.fulfilment.nextStatus
    try {
      const response = await fetch(`/api/admin/orders/${encodeURIComponent(orderId)}/fulfilment/advance`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': session.csrfToken },
        body: JSON.stringify({ expectedCurrentStatus, requestedStatus }),
      })
      const payload = await response.json().catch(() => null) as { message?: string; emailStatus?: string; saved?: boolean; changed?: boolean; currentStatus?: string } | null
      if (response.status === 401) {
        window.location.replace('/admin/login?next=/admin/orders')
        return
      }
      if (!response.ok) {
        if (response.status === 409) await refreshOrder(orderId)
        throw new Error(payload?.message || 'Fulfilment status could not be updated.')
      }
      const refreshed = await refreshOrder(orderId)
      if (shouldOfferNextStopSms(payload, requestedStatus, orderId, refreshed)) setSmsPromptOrder(orderId)
      if (requestedStatus === 'delivered') {
        pushToast('Order marked delivered', deliveredEmailOutcome(payload?.emailStatus))
      } else {
        pushToast('Fulfilment updated', `Order is now ${fulfilmentLabel(requestedStatus)}.`)
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Fulfilment status could not be updated.')
    } finally {
      setBusy(false)
    }
  }

  async function correctSelectedFulfilment() {
    if (!session || !selectedOrder?.fulfilment.status || busy) return
    setFulfilmentCorrectionConfirm(false)
    setBusy(true)
    setError('')
    const orderId = selectedOrder.orderId
    const expectedCurrentStatus = selectedOrder.fulfilment.status
    try {
      const response = await fetch(`/api/admin/orders/${encodeURIComponent(orderId)}/fulfilment/correct`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': session.csrfToken },
        body: JSON.stringify({
          expectedCurrentStatus,
          requestedStatus: fulfilmentTarget,
          reason: fulfilmentReason,
        }),
      })
      const payload = await response.json().catch(() => null) as { message?: string } | null
      if (response.status === 401) {
        window.location.replace('/admin/login?next=/admin/orders')
        return
      }
      if (!response.ok) {
        if (response.status === 409) await refreshOrder(orderId)
        throw new Error(payload?.message || 'Fulfilment correction could not be saved.')
      }
      await refreshOrder(orderId)
      setFulfilmentEditor(null)
      setFulfilmentReason('')
      pushToast('Fulfilment corrected', `Order is now ${fulfilmentLabel(fulfilmentTarget)}. The correction remains in history.`)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Fulfilment correction could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const columns: AdminTableColumn<OrderSummary>[] = [
    {
      key: 'order', label: 'Order', width: '16%',
      render: (row) => <div className="admin-orders__primary-cell"><strong>{row.orderId}</strong><span>{formatDateTime(row.createdAt)}</span></div>,
    },
    {
      key: 'customer', label: 'Customer', width: '22%',
      render: (row) => <div className="admin-orders__primary-cell"><strong>{row.customerName}</strong><span>{row.suburb || 'Suburb not recorded'}</span></div>,
    },
    {
      key: 'delivery', label: 'Delivery',
      render: (row) => <div className="admin-orders__primary-cell"><strong>{deliveryLabel(row.deliveryDay, data?.view, data?.drop.startsAt)}</strong><span>{row.boxCount} {row.boxCount === 1 ? 'box' : 'boxes'}</span></div>,
    },
    {
      key: 'payment', label: 'Payment',
      render: (row) => <div className="admin-orders__status-cell"><PaymentPill status={row.paymentStatus} cancelledAt={row.cancelledAt} overdue={row.bankTransferOverdue} /><span>{row.paymentProvider === 'volley' ? 'Pay via Volley' : paymentMethodLabel(row.paymentMethod)}</span>{row.bankTransferDueAt && row.paymentMethod === 'bank-transfer' && !row.cancelledAt ? <span>2-hour window: {formatDateTime(row.bankTransferDueAt)}</span> : null}</div>,
    },
    { key: 'fulfilment', label: 'Fulfilment', render: (row) => <AdminStatusPill tone={fulfilmentTone(row.fulfilmentStatus)}>{fulfilmentLabel(row.fulfilmentStatus)}</AdminStatusPill> },
    { key: 'total', label: 'Total', align: 'right', render: (row) => <strong className="admin-orders__total">{formatMoney(row.totalCents)}</strong> },
    {
      key: 'action', label: '', align: 'right',
      render: (row) => <AdminButton compact variant="secondary" type="button" disabled={!row.viewable} onClick={(event) => { orderTriggerRef.current = event.currentTarget; void loadOrderDetail(row.orderId) }}>View</AdminButton>,
    },
  ]

  if (error && (!session || !data)) {
    return <main className="admin-shell-gate-error"><AdminStatePanel state="error" title="Orders unavailable" description={error} action={<AdminButton type="button" onClick={() => void loadOrders()}>Retry</AdminButton>} /></main>
  }
  if (!session || !data) {
    return <main className="admin-shell-gate-loading" aria-label="Loading Orders"><AdminCard><AdminSkeleton lines={7} /></AdminCard></main>
  }

  const listTitle = data.view === 'active' ? 'Active orders' : data.view === 'awaiting' ? 'Awaiting payment' : 'Order history'

  return (
    <AdminShell
      title="Orders"
      eyebrow="OPERATIONS"
      description={ordersScopeDescription(data.view, data.drop.startsAt)}
      user={session.user}
      onLogout={() => void logout()}
      logoutBusy={logoutBusy}
      environmentLabel={environmentLabel}
      actions={<AdminButton variant="secondary" type="button" disabled={busy} onClick={() => void Promise.all([loadOrders(), loadLiveProductTotals()])}>Refresh orders</AdminButton>}
    >
      <div className="admin-orders-page">
        {error && <div className="admin-orders__inline-error" role="alert">{error}</div>}

        <AdminCard className="admin-orders__live-totals">
          <div className="admin-orders__live-totals-header">
            <div>
              <span className="admin-orders__live-totals-eyebrow">LIVE PRODUCTION</span>
              <h2>Production totals</h2>
              <p>Current-week production{liveTotals ? ` · ${operationalWeekendLabels(liveTotals.drop.startsAt).saturday} · ${operationalWeekendLabels(liveTotals.drop.startsAt).sunday}` : ''} · cancelled orders and SG01 test excluded</p>
            </div>
            <div className="admin-orders__live-total-count" aria-label={liveTotals ? `${liveTotals.totalCookies} cookies and ${liveTotals.addOns.totalPacks} add-on packs across ${liveTotals.liveOrderCount} live customer orders` : 'Loading live totals'}>
              <strong>{liveTotals ? liveTotals.totalCookies : '—'}</strong>
              <span>Total cookies</span>
              {liveTotals ? <small>{liveTotals.liveOrderCount} live {liveTotals.liveOrderCount === 1 ? 'order' : 'orders'}</small> : null}
            </div>
          </div>

          {liveTotals && (liveTotals.groups.length || liveTotals.addOns.items.length) ? (
            <div className="admin-orders__live-groups">
              {liveTotals.groups.map((group) => (
                <section className="admin-orders__live-group" key={group.category}>
                  <div className="admin-orders__live-group-header">
                    <h3>{liveCategoryLabel(group.category)}</h3>
                    <span>{group.totalCookies} {group.totalCookies === 1 ? 'cookie' : 'cookies'}</span>
                  </div>
                  <div className="admin-orders__live-totals-grid">
                    {group.items.map((item) => (
                      <div className="admin-orders__live-total-item" key={`${group.category}-${item.productName}`}>
                        <span>{item.productName}</span>
                        <strong>{item.quantity}</strong>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
              {liveTotals.addOns.items.length ? (
                <section className="admin-orders__live-group" data-production-group="add-ons">
                  <div className="admin-orders__live-group-header">
                    <h3>Add-ons</h3>
                    <span>{liveTotals.addOns.totalPacks} {liveTotals.addOns.totalPacks === 1 ? 'pack' : 'packs'}</span>
                  </div>
                  <div className="admin-orders__live-totals-grid">
                    {liveTotals.addOns.items.map((item) => (
                      <div className="admin-orders__live-total-item" key={`${item.sourceId}-${item.productName}-${item.packLabel}`}>
                        <span>{item.productName}{item.packLabel ? `, ${item.packLabel}` : ''}</span>
                        <strong>{item.quantity} {item.quantity === 1 ? 'pack' : 'packs'}</strong>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
          ) : (
            <p className="admin-orders__live-totals-empty">{liveTotals ? 'No live production quantities are recorded for this drop yet.' : 'Loading live production totals…'}</p>
          )}

          <div className="admin-orders__live-totals-footer">
            <span>{liveTotals ? `Updated ${formatDateTime(liveTotals.generatedAt)} · Auto-refreshes every 30 seconds` : 'Reading the authoritative order store…'}</span>
            {liveTotalsError ? <strong role="status">{liveTotalsError}</strong> : null}
          </div>
        </AdminCard>

        <section className="admin-orders__summary" aria-label="Order views">
          <button type="button" className={`admin-orders__summary-card ${view === 'active' ? 'is-active' : ''}`} onClick={() => selectView('active')}><span>Active</span><strong>{data.counts.active}</strong><small>Orders in the current drop</small></button>
          <button type="button" className={`admin-orders__summary-card ${view === 'awaiting' ? 'is-active' : ''}`} onClick={() => selectView('awaiting')}><span>Awaiting payment</span><strong>{data.counts.awaiting}</strong><small>Pending orders · all time</small></button>
          <button type="button" className={`admin-orders__summary-card ${view === 'history' ? 'is-active' : ''}`} onClick={() => selectView('history')}><span>History</span><strong>{data.counts.history}</strong><small>Earlier and cancelled orders · all time</small></button>
        </section>

        <AdminCard padding="none" className="admin-orders__list-card">
          <div className="admin-orders__list-header"><div><h2>{listTitle}</h2><p>{data.total} {data.total === 1 ? 'order' : 'orders'} in this view</p></div><VolleyPaymentIssues session={session} onOrderChanged={() => void Promise.all([loadOrders(),loadLiveProductTotals()])}/></div>
          <form onSubmit={submitSearch}>
            <AdminFilterBar actions={<>{search ? <AdminButton variant="ghost" type="button" onClick={() => { setSearchInput(''); setPage(1); setSearch('') }}>Clear</AdminButton> : null}<AdminButton type="submit">Search</AdminButton></>}>
              <label className="admin-orders__search-field"><span className="admin-sr-only">Search orders</span><AdminInput type="search" value={searchInput} maxLength={80} placeholder="Search order, customer, email, phone or suburb" onChange={(event) => setSearchInput(event.target.value)} /></label>
              <AdminField label="Delivery">
                <AdminSelect value={deliveryFilter} onChange={(event) => { setPage(1); setDeliveryFilter(event.target.value as DeliveryFilter) }}>
                  <option value="all">All days</option>
                  <option value="saturday">{deliveryLabel('saturday', data.view, data.drop.startsAt)}</option>
                  <option value="sunday">{deliveryLabel('sunday', data.view, data.drop.startsAt)}</option>
                  <option value="unspecified">Unspecified</option>
                </AdminSelect>
              </AdminField>
              <AdminField label="Fulfilment">
                <AdminSelect value={fulfilmentFilter} onChange={(event) => { setPage(1); setFulfilmentFilter(event.target.value as FulfilmentFilter) }}>
                  <option value="all">All statuses</option>
                  <option value="received">Received</option>
                  <option value="preparing">Preparing</option>
                  <option value="packed">Packed / Ready</option>
                  <option value="out_for_delivery">Out for delivery</option>
                  <option value="delivered">Delivered</option>
                  <option value="legacy">Legacy</option>
                </AdminSelect>
              </AdminField>
            </AdminFilterBar>
          </form>
          {data.orders.length > 0 ? <><AdminDataTable columns={columns} rows={data.orders} rowKey={(row) => `${row.orderId}-${row.createdAt}`} caption="Sugar Gremlin orders" /><div className="admin-orders__pagination-wrap"><AdminPagination page={data.page} totalPages={data.totalPages} onPageChange={setPage} /></div></> : <AdminStatePanel state="empty" title={search ? 'No matching orders' : 'No orders in this view'} description={search ? 'Try a different order number, customer detail or suburb.' : 'Orders appear here automatically after checkout records them.'} />}
        </AdminCard>

        <p className="admin-orders__scope-note">Fulfilment V2 tracks operational truth separately from payment. Legacy orders remain untracked until an owner explicitly starts them at the truthful current status.</p>

        <AdminButton variant="secondary" type="button" onClick={() => setHistoricalCompletionOpen(true)}>Historical order cleanup</AdminButton>
        <HistoricalCompletionPanel open={historicalCompletionOpen} onClose={() => setHistoricalCompletionOpen(false)} onCompleted={() => { void loadOrders() }} />

        <AdminModal
          open={Boolean(selectedOrder)}
          fitVisualViewport
          returnFocusRef={orderTriggerRef}
          closeDisabled={busy}
          closeDisabledReason="Updating orders. You can close this dialog when the update finishes."
          title={selectedOrder ? `Order ${selectedOrder.orderId}` : 'Order'}
          description={selectedOrder ? `${formatDateTime(selectedOrder.createdAt)} · ${selectedOrder.paymentProvider === 'volley' ? 'Pay via Volley' : paymentMethodLabel(selectedOrder.paymentMethod)}` : undefined}
          onClose={() => {
            if (!busy) {
              setSelectedOrder(null)
              setSmsPromptOrder(null)
              setFulfilmentEditor(null)
              setFulfilmentReason('')
              setInternalNoteDraft('')
              setComplimentaryEditorOpen(false)
              setComplimentaryQuantity(1)
              setComplimentaryReason('')
              setComplimentaryRemoveTarget(null)
            }
          }}
          footer={selectedOrder ? (
            <>
              {selectedOrder.canCancel ? <AdminButton variant="danger" type="button" disabled={busy} onClick={() => setCancelConfirm(true)}>{busy ? 'Updating…' : 'Cancel unpaid order'}</AdminButton> : null}
              {selectedOrder.fulfilment.correctionTargets.length ? <AdminButton variant="secondary" type="button" disabled={busy} onClick={() => {
                const targets = selectedOrder.fulfilment.correctionTargets
                setFulfilmentTarget(targets[targets.length - 1])
                setFulfilmentReason('')
                setFulfilmentEditor('correct')
              }}>Correct fulfilment status</AdminButton> : null}
              {selectedOrder.fulfilment.canStart ? <AdminButton variant="secondary" type="button" disabled={busy} onClick={() => {
                setFulfilmentTarget(selectedOrder.fulfilment.startStatuses[0] ?? 'received')
                setFulfilmentEditor('start')
              }}>Start fulfilment tracking</AdminButton> : null}
              {selectedOrder.canMarkPaid ? <AdminButton type="button" disabled={busy} onClick={() => setMarkPaidConfirm(true)}>{busy ? 'Updating…' : 'Mark bank transfer paid'}</AdminButton> : null}
              {selectedOrder.fulfilment.canAdvance ? <AdminButton type="button" disabled={busy} onClick={() => setFulfilmentAdvanceConfirm(true)}>{busy ? 'Updating…' : nextActionLabel(selectedOrder.fulfilment.status)}</AdminButton> : null}
            </>
          ) : undefined}
        >
          {selectedOrder ? <div className="admin-orders__detail">
            <div className="admin-orders__detail-status-row">
              <PaymentPill status={selectedOrder.paymentStatus} cancelledAt={selectedOrder.cancelledAt} overdue={selectedOrder.bankTransferOverdue} />
              {selectedOrder.cancelledAt
                ? <AdminStatusPill tone="danger">Fulfilment locked · {fulfilmentLabel(selectedOrder.fulfilment.status)}</AdminStatusPill>
                : <AdminStatusPill tone={fulfilmentTone(selectedOrder.fulfilment.status)}>{fulfilmentLabel(selectedOrder.fulfilment.status)}</AdminStatusPill>}
            </div>
            <ol className="admin-orders__progress" aria-label="Order progress">
              <li className="is-complete"><strong>1</strong><span>Order received<small>{selectedOrder.fulfilment.tracked ? 'Recorded' : 'Recorded · fulfilment legacy'}</small></span></li>
              <li className={selectedOrder.paymentStatus === 'paid' ? 'is-complete' : selectedOrder.cancelledAt ? '' : 'is-current'}><strong>2</strong><span>Payment<small>{selectedOrder.cancelledAt ? 'Cancelled' : selectedOrder.paymentStatus === 'paid' ? 'Paid' : selectedOrder.bankTransferOverdue ? '2-hour window expired — manual review' : 'Awaiting payment'}</small></span></li>
              <li className={progressClass(selectedOrder.fulfilment.status, 'preparing')}><strong>3</strong><span>Preparing<small>{selectedOrder.fulfilment.status === 'preparing' ? 'Current' : progressClass(selectedOrder.fulfilment.status, 'preparing') === 'is-complete' ? 'Complete' : 'Pending'}</small></span></li>
              <li className={progressClass(selectedOrder.fulfilment.status, 'packed')}><strong>4</strong><span>Packed / Ready<small>{selectedOrder.fulfilment.status === 'packed' ? 'Current' : progressClass(selectedOrder.fulfilment.status, 'packed') === 'is-complete' ? 'Complete' : 'Pending'}</small></span></li>
              <li className={progressClass(selectedOrder.fulfilment.status, 'out_for_delivery')}><strong>5</strong><span>Out for delivery<small>{selectedOrder.fulfilment.status === 'out_for_delivery' ? 'Current' : progressClass(selectedOrder.fulfilment.status, 'out_for_delivery') === 'is-complete' ? 'Complete' : 'Pending'}</small></span></li>
              <li className={progressClass(selectedOrder.fulfilment.status, 'delivered')}><strong>6</strong><span>Delivered<small>{selectedOrder.fulfilment.status === 'delivered' ? 'Current' : 'Pending'}</small></span></li>
            </ol>

            {selectedOrder.deliverySms ? <section className="admin-orders__sms" aria-label="Delivery SMS">
              <h3>{smsPromptOrder === selectedOrder.orderId && selectedOrder.deliverySms.canSend ? 'Notify customer by SMS?' : 'Next-stop delivery SMS'}</h3>
              <p role="status"><strong>{({ 'not-sent': 'Not sent', sending: 'Sending', accepted: 'Accepted', delivered: 'Delivered', failed: 'Failed / blocked', unknown: 'Outcome unknown', disabled: 'Disabled', unavailable: 'Unavailable' })[selectedOrder.deliverySms.state]}</strong> · {selectedOrder.deliverySms.message}</p>
              {selectedOrder.deliverySms.canSend && smsPromptOrder === selectedOrder.orderId ? <>
                <p>Send only if this is your actual NEXT delivery stop. Press Send SMS to confirm. Check replies only while safely parked.</p>
                <p>Delivery may be delayed. Once submitted, the SMS cannot be cancelled or expired.</p>
                <blockquote>{NEXT_STOP_SMS_TEXT}</blockquote>
                <p>{selectedOrder.deliverySms.recipientHint} · Replies go to hello@sugargremlin.co.nz. Customer carrier reply charges may apply. Changed instructions need an owner’s acknowledgement.</p>
                <div className="admin-orders__editor-actions">
                  <AdminButton type="button" disabled={busy} onClick={() => void submitSmsAction('send')}>{busy ? 'Sending…' : 'Send SMS'}</AdminButton>
                  <AdminButton type="button" variant="ghost" disabled={busy} onClick={() => setSmsPromptOrder(null)}>Not now</AdminButton>
                </div>
              </> : selectedOrder.deliverySms.canSend ? <AdminButton type="button" variant="secondary" disabled={busy} onClick={() => void reviewNextStopSms()}>Notify next customer</AdminButton> : null}
              {selectedOrder.deliverySms.canCheckStatus || (selectedOrder.deliverySms.state === 'not-sent' && !selectedOrder.deliverySms.canSend)
                ? <AdminButton type="button" variant="secondary" disabled={busy} onClick={() => void submitSmsAction('check-status')}>Check SMS status</AdminButton> : null}
            </section> : null}

            {fulfilmentEditor === 'start' ? <AdminCard className="admin-orders__fulfilment-editor">
              <h3>Start fulfilment tracking</h3>
              <p>This legacy order has no invented history. Choose the truthful status it is at now.</p>
              <AdminField label="Current fulfilment status">
                <AdminSelect value={fulfilmentTarget} onChange={(event) => setFulfilmentTarget(event.target.value as FulfilmentStatus)}>
                  {selectedOrder.fulfilment.startStatuses.map((status) => <option key={status} value={status}>{fulfilmentLabel(status)}</option>)}
                </AdminSelect>
              </AdminField>
              <div className="admin-orders__editor-actions">
                <AdminButton variant="ghost" type="button" disabled={busy} onClick={() => setFulfilmentEditor(null)}>Cancel</AdminButton>
                <AdminButton type="button" disabled={busy} onClick={() => setFulfilmentStartConfirm(true)}>Review start</AdminButton>
              </div>
            </AdminCard> : null}

            {fulfilmentEditor === 'correct' ? <AdminCard className="admin-orders__fulfilment-editor">
              <h3>Correct fulfilment status</h3>
              <p>Corrections move backwards only and remain permanently visible in fulfilment history.</p>
              <AdminField label="Correct status to">
                <AdminSelect value={fulfilmentTarget} onChange={(event) => setFulfilmentTarget(event.target.value as FulfilmentStatus)}>
                  {selectedOrder.fulfilment.correctionTargets.map((status) => <option key={status} value={status}>{fulfilmentLabel(status)}</option>)}
                </AdminSelect>
              </AdminField>
              <AdminField label="Reason" required>
                <AdminInput value={fulfilmentReason} maxLength={240} placeholder="Why is this status being corrected?" onChange={(event) => setFulfilmentReason(event.target.value)} />
              </AdminField>
              <div className="admin-orders__editor-actions">
                <AdminButton variant="ghost" type="button" disabled={busy} onClick={() => { setFulfilmentEditor(null); setFulfilmentReason('') }}>Cancel</AdminButton>
                <AdminButton type="button" disabled={busy || !fulfilmentReason.trim()} onClick={() => setFulfilmentCorrectionConfirm(true)}>Review correction</AdminButton>
              </div>
            </AdminCard> : null}

            <div className="admin-orders__detail-panels">
              <section><h3>Customer</h3><p><strong>{selectedOrder.customer.fullName || '—'}</strong><br />{selectedOrder.customer.email || '—'}<br />{selectedOrder.customer.phone || '—'}</p></section>
              <section><h3>Delivery</h3><p><strong>{deliveryLabel(selectedOrder.delivery.day)}</strong><br />{selectedOrder.delivery.address || '—'}{selectedOrder.delivery.suburb ? `, ${selectedOrder.delivery.suburb}` : ''}{selectedOrder.delivery.postcode ? ` ${selectedOrder.delivery.postcode}` : ''}<br />Notes: {selectedOrder.delivery.notes || 'None'}</p></section>
              <section><h3>Payment</h3><p><strong>{selectedOrder.cancelledAt ? 'Cancelled' : selectedOrder.paymentStatus === 'paid' ? 'Paid' : selectedOrder.bankTransferOverdue ? 'Payment window expired — manual review' : 'Awaiting payment'}</strong><br />{selectedOrder.paymentProvider === 'volley' ? 'Pay via Volley' : paymentMethodLabel(selectedOrder.paymentMethod)}{selectedOrder.paymentReference ? <><br />Reference: {selectedOrder.paymentReference}</> : null}{selectedOrder.bankTransferDueAt && selectedOrder.paymentMethod === 'bank-transfer' ? <><br />2-hour deadline: {formatDateTime(selectedOrder.bankTransferDueAt)}</> : null}{selectedOrder.cancelledAt ? <><br />Cancelled: {formatDateTime(selectedOrder.cancelledAt)}</> : null}</p></section>
            </div>

            <section className="admin-orders__internal-note" aria-labelledby="admin-order-internal-note-title">
              <div className="admin-orders__operational-heading">
                <div>
                  <h3 id="admin-order-internal-note-title">Internal note</h3>
                  <p>Boss Zone only · never shown to the customer</p>
                </div>
                {selectedOrder.internalNote ? <span>Updated {formatDateTime(selectedOrder.internalNote.updatedAt)} · {selectedOrder.internalNote.updatedBy}</span> : null}
              </div>
              <textarea
                className="admin-ui-input admin-orders__note-input"
                value={internalNoteDraft}
                maxLength={750}
                rows={4}
                placeholder="Customer contacted us about…"
                onChange={(event) => setInternalNoteDraft(event.target.value)}
              />
              <div className="admin-orders__note-actions">
                <span>{internalNoteDraft.length}/750</span>
                <AdminButton
                  compact
                  type="button"
                  disabled={busy || internalNoteDraft.trim() === (selectedOrder.internalNote?.text ?? '')}
                  onClick={() => void saveInternalNote()}
                >
                  {busy ? 'Saving…' : 'Save internal note'}
                </AdminButton>
              </div>
            </section>

            {selectedOrder.measurement ? (
              <section className="admin-orders__box-section" aria-labelledby="admin-order-measurement-title">
                <h3 id="admin-order-measurement-title">Advertising measurement</h3>
                <div className="admin-orders__detail-panels">
                  <section>
                    <h3>Eligibility</h3>
                    <p>
                      <strong>{measurementEligibilityLabel(selectedOrder.measurement.eligible)}</strong>
                      <br />Basis: {selectedOrder.measurement.consentBasis ? measurementStateLabel(selectedOrder.measurement.consentBasis) : 'Not recorded'}
                    </p>
                  </section>
                  <section>
                    <h3>Browser Purchase</h3>
                    <p>
                      <strong>{measurementStateLabel(selectedOrder.measurement.browserPurchase.state)}</strong>
                      {selectedOrder.measurement.browserPurchase.attemptedAt ? <><br />Queued/attempted: {formatDateTime(selectedOrder.measurement.browserPurchase.attemptedAt)}</> : null}
                    </p>
                  </section>
                  <section>
                    <h3>Server Purchase</h3>
                    <p>
                      <strong>{measurementStateLabel(selectedOrder.measurement.serverPurchase.state)}</strong>
                      {selectedOrder.measurement.serverPurchase.eventId ? <><br />Event ID: {selectedOrder.measurement.serverPurchase.eventId}</> : null}
                      <br />CAPI attempts: {selectedOrder.measurement.serverPurchase.attempts}
                      {selectedOrder.measurement.serverPurchase.lastAttemptAt ? <><br />Last attempt: {formatDateTime(selectedOrder.measurement.serverPurchase.lastAttemptAt)}</> : null}
                      {selectedOrder.measurement.serverPurchase.sentAt ? <><br />Sent: {formatDateTime(selectedOrder.measurement.serverPurchase.sentAt)}</> : null}
                    </p>
                  </section>
                </div>
                <div className="admin-orders__detail-panels">
                  <section>
                    <h3>Meta response</h3>
                    <p>
                      {selectedOrder.measurement.serverPurchase.providerEventsReceived !== null
                        ? <>Accepted events: {selectedOrder.measurement.serverPurchase.providerEventsReceived}</>
                        : 'No provider acknowledgement recorded.'}
                      {selectedOrder.measurement.serverPurchase.providerHttpStatus !== null ? <><br />HTTP: {selectedOrder.measurement.serverPurchase.providerHttpStatus}</> : null}
                      {selectedOrder.measurement.serverPurchase.providerErrorCode ? <><br />Error: {selectedOrder.measurement.serverPurchase.providerErrorCode}</> : null}
                      {selectedOrder.measurement.serverPurchase.providerErrorMessage ? <><br />{selectedOrder.measurement.serverPurchase.providerErrorMessage}</> : null}
                    </p>
                  </section>
                  <section>
                    <h3>Attribution</h3>
                    <p>
                      UTM source: {selectedOrder.measurement.attribution.utmSource || '—'}
                      <br />UTM campaign: {selectedOrder.measurement.attribution.utmCampaign || '—'}
                      <br />UTM content: {selectedOrder.measurement.attribution.utmContent || '—'}
                      <br />Meta click identified: {selectedOrder.measurement.attribution.metaClickIdentified ? 'Yes' : 'No'}
                      <br />Browser identifier present: {selectedOrder.measurement.attribution.browserIdentifierPresent ? 'Yes' : 'No'}
                    </p>
                  </section>
                </div>
              </section>
            ) : null}

            <section className="admin-orders__box-section">
              <h3>Order summary</h3>
              {selectedOrder.boxes.map((box, boxIndex) => (
                <article className="admin-orders__box" key={`${box.quantity}-${boxIndex}`}>
                  <div className="admin-orders__box-heading"><strong>Mix &amp; Match {box.quantity}-Pack</strong><span>{formatMoney(box.priceCents)}</span></div>
                  <ul>{box.selections.map((selection, selectionIndex) => <li key={`${selection.productName}-${selectionIndex}`}><span>{selection.productName} × {selection.quantity}{selection.category ? <small>{selection.category}</small> : null}</span><em>{selection.pricingLabel}</em></li>)}</ul>
                </article>
              ))}
              {selectedOrder.addons.length ? (
                <article className="admin-orders__addons">
                  <h4>PAID ADD-ONS</h4>
                  <ul>{selectedOrder.addons.map((item) => <li key={item.id}><span>{item.name}{item.packLabel ? ` — ${item.packLabel}` : ''} × {item.quantity}</span><strong>{formatMoney(item.unitPriceCents * item.quantity)}</strong></li>)}</ul>
                </article>
              ) : null}

              <article className="admin-orders__complimentary">
                <div className="admin-orders__operational-heading">
                  <div>
                    <h4>COMPLIMENTARY ITEMS</h4>
                    <p>Pack these with the order · customer charge stays $0.00</p>
                  </div>
                  {!selectedOrder.cancelledAt ? <AdminButton compact variant="secondary" type="button" disabled={busy} onClick={() => {
                    if (!complimentarySelection && complimentaryCatalogue[0]) setComplimentarySelection(complimentaryKey(complimentaryCatalogue[0]))
                    setComplimentaryEditorOpen(true)
                  }}>+ Add complimentary item</AdminButton> : null}
                </div>

                {selectedOrder.complimentaryItems.length ? (
                  <ul className="admin-orders__complimentary-list">
                    {selectedOrder.complimentaryItems.map((item) => (
                      <li key={item.id}>
                        <div>
                          <strong>{item.name}{item.packLabel ? ` · ${item.packLabel}` : ''} ×{item.quantity}</strong>
                          <span>Reason: {item.reason}</span>
                          <small>Added {formatDateTime(item.createdAt)} · {item.createdBy}</small>
                        </div>
                        <div className="admin-orders__complimentary-status">
                          <AdminStatusPill tone="pink">COMPLIMENTARY</AdminStatusPill>
                          <strong>FREE · {formatMoney(0)}</strong>
                          <AdminButton compact variant="ghost" type="button" disabled={busy} onClick={() => setComplimentaryRemoveTarget(item)}>Remove</AdminButton>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : <p className="admin-orders__complimentary-empty">No complimentary items recorded.</p>}

                {complimentaryEditorOpen ? (
                  <AdminCard className="admin-orders__complimentary-editor">
                    <h4>Add complimentary item</h4>
                    <div className="admin-orders__complimentary-fields">
                      <AdminField label="Product / add-on" required>
                        <AdminSelect value={complimentarySelection} onChange={(event) => setComplimentarySelection(event.target.value)}>
                          {complimentaryCatalogue.map((item) => (
                            <option key={complimentaryKey(item)} value={complimentaryKey(item)}>
                              {item.group}: {item.name}{item.packLabel ? ` · ${item.packLabel}` : ''}
                            </option>
                          ))}
                        </AdminSelect>
                      </AdminField>
                      <AdminField label="Quantity" required>
                        <AdminInput type="number" min={1} max={20} value={complimentaryQuantity} onChange={(event) => setComplimentaryQuantity(Math.max(1, Math.min(20, Number(event.target.value) || 1)))} />
                      </AdminField>
                    </div>
                    <AdminField label="Reason" required hint="Internal only. Required so packing staff know why it is free.">
                      <AdminInput value={complimentaryReason} maxLength={240} placeholder="Customer recovery, promo code issue" onChange={(event) => setComplimentaryReason(event.target.value)} />
                    </AdminField>
                    <div className="admin-orders__editor-actions">
                      <AdminButton variant="ghost" type="button" disabled={busy} onClick={() => { setComplimentaryEditorOpen(false); setComplimentaryReason(''); setComplimentaryQuantity(1) }}>Cancel</AdminButton>
                      <AdminButton type="button" disabled={busy || !complimentarySelection || !complimentaryReason.trim()} onClick={() => void addComplimentaryItem()}>{busy ? 'Saving…' : 'Add free item'}</AdminButton>
                    </div>
                  </AdminCard>
                ) : null}

                {selectedOrder.complimentaryHistory.length ? (
                  <details className="admin-orders__complimentary-history">
                    <summary>Removed complimentary items ({selectedOrder.complimentaryHistory.length})</summary>
                    <ul>{selectedOrder.complimentaryHistory.map((item) => (
                      <li key={item.id}>{item.name}{item.packLabel ? ` · ${item.packLabel}` : ''} ×{item.quantity} · removed {item.removedAt ? formatDateTime(item.removedAt) : '—'} by {item.removedBy || 'Unknown owner'}</li>
                    ))}</ul>
                  </details>
                ) : null}
              </article>

              <dl className="admin-orders__totals">
                <div><dt>Subtotal</dt><dd>{formatMoney(selectedOrder.subtotalCents)}</dd></div>
                {selectedOrder.discountCents > 0 ? <div><dt>Discount{selectedOrder.discountCode ? ` (${selectedOrder.discountCode})` : ''}</dt><dd>-{formatMoney(selectedOrder.discountCents)}</dd></div> : null}
                <div><dt>Delivery</dt><dd>{formatMoney(selectedOrder.deliveryFeeCents)}</dd></div>
                <div className="is-total"><dt>Total</dt><dd>{formatMoney(selectedOrder.totalCents)}</dd></div>
              </dl>
            </section>
            <section className="admin-orders__fulfilment-history">
              <div className="admin-orders__history-heading"><h3>Fulfilment history</h3><span>Pacific/Auckland</span></div>
              {selectedOrder.fulfilment.history.length ? <ol>
                {selectedOrder.fulfilment.history.map((event, index) => <li key={`${event.createdAt}-${index}`}>
                  <div><strong>{fulfilmentEventLabel(event)}</strong><span>{formatDateTime(event.createdAt)} · {event.actor}</span></div>
                  {event.reason ? <p>Reason: {event.reason}</p> : null}
                </li>)}
              </ol> : <p>No fulfilment history. This is a legacy order until tracking is explicitly started.</p>}
            </section>
            <p className="admin-orders__detail-note">{selectedOrder.fulfilment.message}</p>
          </div> : null}
        </AdminModal>

        <AdminConfirmDialog fitVisualViewport closeDisabled={busy} open={markPaidConfirm} title={selectedOrder ? `Mark ${selectedOrder.orderId} as paid?` : 'Mark bank transfer paid?'} description={selectedOrder ? `This records the authoritative bank-transfer payment as paid for ${formatMoney(selectedOrder.totalCents)} and triggers the approved Payment Received email exactly once with safe retry behaviour.` : ''} confirmLabel="Mark paid" onConfirm={() => void markSelectedOrderPaid()} onCancel={() => setMarkPaidConfirm(false)} />
        <AdminConfirmDialog fitVisualViewport closeDisabled={busy} open={cancelConfirm} title={selectedOrder ? `Cancel unpaid order ${selectedOrder.orderId}?` : 'Cancel unpaid bank-transfer order?'} description={selectedOrder ? `The 2-hour payment window has expired. This action cancels the order, releases its capacity, and sends the customer the cancellation email. Nothing is cancelled automatically at the deadline.` : ''} confirmLabel="Cancel order" dangerous onConfirm={() => void cancelSelectedBankTransfer()} onCancel={() => setCancelConfirm(false)} />
        <AdminConfirmDialog fitVisualViewport closeDisabled={busy}
          open={fulfilmentAdvanceConfirm}
          title={selectedOrder?.fulfilment.nextStatus ? `${nextActionLabel(selectedOrder.fulfilment.status)} for ${selectedOrder.orderId}?` : 'Update fulfilment?'}
          description={selectedOrder?.fulfilment.nextStatus
            ? selectedOrder.fulfilment.nextStatus === 'delivered'
              ? 'This marks the order as Delivered and triggers the customer Delivered email. The email is sent once, with safe retry if delivery fails.'
              : `This moves the authoritative fulfilment status from ${fulfilmentLabel(selectedOrder.fulfilment.status)} to ${fulfilmentLabel(selectedOrder.fulfilment.nextStatus)}. No customer email is sent.`
            : ''}
          confirmLabel={selectedOrder?.fulfilment.nextStatus ? nextActionLabel(selectedOrder.fulfilment.status) : 'Update'}
          onConfirm={() => void advanceSelectedFulfilment()}
          onCancel={() => setFulfilmentAdvanceConfirm(false)}
        />
        <AdminConfirmDialog fitVisualViewport closeDisabled={busy}
          open={fulfilmentStartConfirm}
          title={selectedOrder ? `Start ${selectedOrder.orderId} at ${fulfilmentLabel(fulfilmentTarget)}?` : 'Start fulfilment tracking?'}
          description={fulfilmentTarget === 'delivered'
            ? 'This creates the first truthful fulfilment event for this legacy order at Delivered and triggers the customer Delivered email once with safe retry.'
            : 'This creates the first truthful fulfilment event for this legacy order. Earlier fulfilment steps will not be invented.'}
          confirmLabel="Start tracking"
          onConfirm={() => void startSelectedFulfilment()}
          onCancel={() => setFulfilmentStartConfirm(false)}
        />
        <AdminConfirmDialog fitVisualViewport closeDisabled={busy} open={fulfilmentCorrectionConfirm} title={selectedOrder ? `Correct ${selectedOrder.orderId} to ${fulfilmentLabel(fulfilmentTarget)}?` : 'Correct fulfilment status?'} description={`This correction is permanent in history. Reason: ${fulfilmentReason.trim() || 'No reason entered'}`} confirmLabel="Save correction" onConfirm={() => void correctSelectedFulfilment()} onCancel={() => setFulfilmentCorrectionConfirm(false)} />
        <AdminConfirmDialog fitVisualViewport closeDisabled={busy}
          open={Boolean(complimentaryRemoveTarget)}
          title={complimentaryRemoveTarget ? `Remove complimentary ${complimentaryRemoveTarget.name}?` : 'Remove complimentary item?'}
          description="The item will stop appearing in the packing list, but the removal remains recorded for audit history."
          confirmLabel="Remove complimentary item"
          dangerous
          onConfirm={() => void removeComplimentaryItem()}
          onCancel={() => setComplimentaryRemoveTarget(null)}
        />
        <AdminToastRegion toasts={toasts} onDismiss={(id) => setToasts((current) => current.filter((toast) => toast.id !== id))} />
      </div>
    </AdminShell>
  )
}
